import { computed } from 'vue';
import { storeToRefs } from 'pinia';

import { useAiMetadataStore } from '@/modules/viewer/store/useAiMetadataStore';
import { useAiPlaybackStore } from '@/modules/viewer/store/useAiPlaybackStore';
import { useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';
import { formatDuration } from '@/helpers/formatDuration';

/**
 * AI Playback (SW-6793): AI Actions panel helpers for Skip Filler Words /
 * Skip Silences — the toggles, the detected counts, and the effective-
 * runtime label. Owner-only (mounted from the panel), safe to remount
 * freely since it holds no effect state of its own. The actual seek-on-
 * playback effect lives in useAiPlaybackEffect, mounted once from Video.vue
 * for both the owner and a guest.
 */
export const useAiPlayback = () => {
  const aiMetadataStore = useAiMetadataStore();
  const itemStore = useViewerItemStore();
  const playbackStore = useAiPlaybackStore();

  const { fillerWordsCount, fillerWordsTotalSeconds, silencesCount, silencesTotalSeconds, skipFillerWords, skipSilences } = storeToRefs(aiMetadataStore);
  const { videoDuration } = storeToRefs(itemStore);
  const { fillerWordsSkipped, silenceSecondsSkipped } = storeToRefs(playbackStore);

  const hasFillerWords = computed(() => fillerWordsCount.value > 0);
  const hasSilences = computed(() => silencesCount.value > 0);

  // Skipping happens by seeking during playback, not by editing the file, so
  // the player's own timeline still reflects the real (unshortened) file
  // duration. This surfaces what the runtime actually works out to once the
  // enabled skips are subtracted, without trying to remap the scrubber itself.
  const effectiveDurationLabel = computed(() => {
    if (!videoDuration.value || (!skipFillerWords.value && !skipSilences.value)) {
      return null;
    }

    const skippedSeconds = (skipFillerWords.value ? fillerWordsTotalSeconds.value : 0) + (skipSilences.value ? silencesTotalSeconds.value : 0);
    const effectiveSeconds = Math.max(0, videoDuration.value - skippedSeconds);

    return `${formatDuration(effectiveSeconds)} effective runtime (was ${formatDuration(videoDuration.value)})`;
  });

  // Owner-only (enforced server-side by AiMetadataController#update) — the
  // setting is item data, not a client preference, so it's saved rather than
  // just flipped locally: it needs to survive a reload and apply to a
  // guest's playback too, not just this tab's.
  const toggleSkipFillerWords = () => aiMetadataStore.updateSkipSettings({ skipFillerWords: !skipFillerWords.value });
  const toggleSkipSilences = () => aiMetadataStore.updateSkipSettings({ skipSilences: !skipSilences.value });

  return {
    hasFillerWords,
    hasSilences,
    fillerWordsCount,
    silencesCount,
    silencesTotalSeconds,
    effectiveDurationLabel,
    skipFillerWords,
    skipSilences,
    fillerWordsSkipped,
    silenceSecondsSkipped,
    toggleSkipFillerWords,
    toggleSkipSilences,
  };
};
